import { qk, useApiQuery, type DashboardMetric, type StaffDashboard } from '@legalcrm/api';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BUBBLE_SECONDS, CornerMascot, WALK_SECONDS } from './AssistantMascot';
import { useAuth } from '@/auth/AuthProvider';

/**
 * The assistant's corner: the lawyer, his speech bubble, and the update card he slides
 * out of his folder.
 *
 * - He walks in once per browser session saying "Need updates?", then says something that
 *   fits the moment (the time of day, the page, what is waiting for this person).
 * - Hover over him, or focus him: he holds his folder out and a card with this person's day comes
 *   out of it. Each line asks the assistant about that line.
 * - The x on the bubble keeps him quiet for the rest of the day.
 * - While someone is typing in a form he holds still and says nothing.
 *
 * The figures are the "My day" ones the dashboard already shows this person -- the same
 * request, the same permissions -- so the card can never show more than they may see.
 */

/** He walks in once per browser session, not on every page load. */
const WALKED_KEY = 'assistant-mascot-walked';
/** The day he was asked to keep quiet, as YYYY-MM-DD in the person's own time zone. */
const QUIET_KEY = 'assistant-mascot-quiet';

const QUESTIONS: Record<string, string> = {
  critical_dates: 'Which critical dates are at risk?',
  documents_to_review: 'Which documents are waiting for review?',
  payments_awaiting_verification: 'Which payments are waiting to be verified?',
  my_tasks: 'What are my open tasks, and which are overdue?',
};

const TONE_DOT: Record<string, string> = {
  critical: 'bg-danger',
  warning: 'bg-warning',
  positive: 'bg-success',
  neutral: 'bg-ink-subtle',
};

type Phase = 'walking' | 'talking' | 'idle';

function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string) {
  try {
    storage().setItem(key, value);
  } catch {
    // No storage (private window, blocked): he simply forgets. Harmless.
  }
}

const local = () => window.localStorage;
const session = () => window.sessionStorage;

function prefersLessMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Needs attention: anything the dashboard colours as a warning or worse. */
function waiting(metrics: DashboardMetric[] | undefined): number {
  return (metrics ?? []).filter((m) => m.tone === 'critical' || m.tone === 'warning').length;
}

function lineFor(path: string, firstName: string, metrics: DashboardMetric[] | undefined): string {
  if (/^\/cases\/[0-9a-f-]{36}/.test(path)) return 'Want a quick summary of this matter?';
  if (/^\/clients\/[0-9a-f-]{36}/.test(path)) return 'Shall I check what is still needed from this client?';
  if (!metrics) return 'Need a hand with something?';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
  const hello = firstName ? `${greeting}, ${firstName}.` : `${greeting}.`;
  const n = waiting(metrics);
  if (n === 0) return `${hello} All clear today. Ask me anything.`;
  return `${hello} ${n} ${n === 1 ? 'thing needs' : 'things need'} you today.`;
}

/** Someone is typing in a field somewhere on the page (outside the corner itself). */
function useSomeoneTyping(corner: React.RefObject<HTMLElement | null>): boolean {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isField = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      !corner.current?.contains(el) &&
      (el.matches('input:not([type=button]):not([type=submit]):not([type=checkbox]):not([type=radio]), textarea, select') ||
        el.isContentEditable);
    let timer = 0;
    const onIn = (e: FocusEvent) => {
      if (isField(e.target)) {
        window.clearTimeout(timer);
        setTyping(true);
      }
    };
    const onOut = () => {
      // Moving from one field to the next should not wake him in between.
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setTyping(isField(document.activeElement)), 400);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, [corner]);
  return typing;
}

/** Three dots, then the words typed out -- as if he were saying it. */
function Typed({ text, instant }: { text: string; instant: boolean }) {
  const [shown, setShown] = useState(instant ? text.length : -1);
  useEffect(() => {
    if (instant) {
      setShown(text.length);
      return;
    }
    setShown(-1);
    let interval = 0;
    const start = window.setTimeout(() => {
      let n = 0;
      interval = window.setInterval(() => {
        n += 1;
        setShown(n);
        if (n >= text.length) window.clearInterval(interval);
      }, 22);
    }, 550);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(interval);
    };
  }, [text, instant]);

  if (shown < 0) {
    return (
      <span className="inline-flex h-5 items-center gap-1 text-ink-muted" aria-hidden="true">
        <span className="m-dot" />
        <span className="m-dot" />
        <span className="m-dot" />
      </span>
    );
  }
  return (
    <span>
      {text.slice(0, shown)}
      {/* The rest, invisible, so the bubble keeps its size while the words appear. */}
      <span className="invisible">{text.slice(shown)}</span>
    </span>
  );
}

export function AssistantCorner({
  onOpen,
  onAsk,
}: {
  onOpen: () => void;
  onAsk: (question: string) => void;
}) {
  const { me } = useAuth();
  const location = useLocation();
  const dashboard = useApiQuery<StaffDashboard>(qk.dashboard(), '/dashboard');
  const metrics = dashboard.data?.metrics;
  const firstName = me?.user.full_name?.split(' ')[0] ?? '';

  const corner = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(0);
  const [quiet, setQuiet] = useState(() => read(local, QUIET_KEY) === today());
  const [phase, setPhase] = useState<Phase>(() =>
    read(session, WALKED_KEY) === '1' || read(local, QUIET_KEY) === today() ? 'idle' : 'walking',
  );
  const [peek, setPeek] = useState(false);
  const closing = useRef(0);
  // After the x, ignore the pointer until it leaves, or the card would reopen at once.
  const suppressed = useRef(false);
  const typing = useSomeoneTyping(corner);
  const instant = prefersLessMotion();

  useEffect(() => {
    if (phase !== 'walking') return;
    write(session, WALKED_KEY, '1');
    const talk = window.setTimeout(() => setPhase('talking'), (WALK_SECONDS + 0.2) * 1000);
    const rest = window.setTimeout(
      () => setPhase('idle'),
      (WALK_SECONDS + BUBBLE_SECONDS) * 1000,
    );
    return () => {
      window.clearTimeout(talk);
      window.clearTimeout(rest);
    };
  }, [phase]);

  useEffect(() => () => window.clearTimeout(closing.current), []);

  // The bubble sits above the card when both show, so it needs the card's height.
  useEffect(() => {
    const el = card.current;
    if (!el) return;
    const measure = () => setCardHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  function open() {
    window.clearTimeout(closing.current);
    if (!suppressed.current) setPeek(true);
  }
  function close(delay = 180) {
    window.clearTimeout(closing.current);
    closing.current = window.setTimeout(() => setPeek(false), delay);
  }
  /** Leaving for the panel: fold the card away and keep it shut until the pointer leaves. */
  function handOff(then: () => void) {
    window.clearTimeout(closing.current);
    setPeek(false);
    suppressed.current = true;
    if (phase !== 'idle') setPhase('idle');
    then();
  }
  function hush() {
    write(local, QUIET_KEY, today());
    setQuiet(true);
    setPhase('idle');
    setPeek(false);
    suppressed.current = true;
  }

  const showCard = peek && !typing;
  const showBubble =
    peek || (!quiet && !typing && (phase === 'walking' || phase === 'talking'));
  const line = phase === 'walking' ? 'Need updates?' : lineFor(location.pathname, firstName, metrics);

  return (
    <div
      ref={corner}
      className={[
        'fixed bottom-2 right-1 z-30',
        showCard ? 'm-peek' : '',
        typing && !peek ? 'm-hush' : '',
      ].join(' ')}
      onPointerLeave={() => {
        suppressed.current = false;
        close();
      }}
      onBlur={(event) => {
        if (!corner.current?.contains(event.relatedTarget as Node | null)) close(0);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') close(0);
      }}
    >
      {/* Bubble above, card below, both to his left. Placed apart rather than stacked, so
          the hidden card takes no room. */}
      <div
        className={[
          'pointer-events-none absolute right-[calc(100%-4px)] transition-[bottom] duration-300',
          phase === 'walking' ? 'm-bubble-follow' : '',
        ].join(' ')}
        style={{ bottom: showCard ? cardHeight + 14 : 132 }}
      >
        <div
          className="m-bubble relative flex w-max max-w-[17rem] items-start gap-2 rounded-2xl border border-line bg-surface py-2 pl-3.5 pr-2 text-sm text-ink shadow-lg"
          data-shown={showBubble ? 'true' : 'false'}
          role="status"
        >
          <div className="min-w-0 py-0.5">
            <p className="font-medium leading-snug">
              <Typed key={line} text={line} instant={instant || peek} />
            </p>
            {phase !== 'walking' && !showCard && (
              <p className="mt-0.5 text-xs text-ink-muted">Click me to find out</p>
            )}
          </div>
          {phase !== 'walking' && (
            <button
              type="button"
              onClick={hush}
              aria-label="Hide for today"
              title="Hide for today"
              className="-mr-0.5 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-ink-subtle hover:bg-surface-sunken hover:text-ink focus-visible:outline-offset-1"
            >
              <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden="true">
                <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          )}
          {/* The tail, pointing at him. */}
          <span
            aria-hidden="true"
            className={[
              'absolute -right-[6px] h-3 w-3 rotate-45 border-r border-t border-line bg-surface',
              showCard ? 'top-3' : 'bottom-3',
            ].join(' ')}
          />
        </div>
      </div>

      <div
        className="pointer-events-none absolute bottom-1 right-[calc(100%-4px)]"
        onPointerEnter={open}
        onFocus={open}
      >
        <div
          ref={card}
          className="m-card w-64 rounded-xl border border-line bg-surface p-1.5 text-sm shadow-xl"
          data-shown={showCard ? 'true' : 'false'}
        >
          <p className="flex items-center justify-between px-2 pb-1 pt-1 text-[11px] font-medium uppercase tracking-wider text-ink-subtle">
            <span>From your day</span>
            <span className="font-normal normal-case tracking-normal">
              {new Date().toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </p>
          {dashboard.isLoading && (
            <div className="space-y-1.5 px-2 py-1.5" aria-hidden="true">
              <div className="h-4 w-4/5 animate-pulse rounded bg-surface-sunken" />
              <div className="h-4 w-3/5 animate-pulse rounded bg-surface-sunken" />
            </div>
          )}
          {!dashboard.isLoading && !metrics && (
            <p className="px-2 py-1.5 text-ink-muted">Your day could not be loaded. Ask me instead.</p>
          )}
          {metrics?.map((metric) => (
            <button
              key={metric.key}
              type="button"
              onClick={() =>
                handOff(() =>
                  onAsk(QUESTIONS[metric.key] ?? `Tell me about ${metric.label.toLowerCase()}.`),
                )
              }
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-surface-sunken focus-visible:outline-offset-0"
            >
              <span className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT[metric.tone ?? 'neutral']}`} />
              <span className="min-w-0 flex-1 truncate text-ink">
                {metric.label}
                {metric.caption && <span className="text-ink-muted"> · {metric.caption}</span>}
              </span>
              <span className="font-semibold tabular-nums text-ink">{metric.value}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => handOff(onOpen)}
            className="mt-1 flex w-full items-center justify-between rounded-lg border-t border-line px-2 pb-1 pt-2 text-left text-primary hover:bg-surface-sunken focus-visible:outline-offset-0"
          >
            Ask something else
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>

      <button
        type="button"
        aria-label="Ask the assistant"
        onClick={() => handOff(onOpen)}
        onPointerEnter={open}
        onFocus={open}
        className="block rounded-2xl focus-visible:outline-offset-2"
      >
        <CornerMascot walking={phase !== 'idle'} className="drop-shadow-md" />
      </button>
    </div>
  );
}
